"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "default", {
    enumerable: true,
    get: function() {
        return DependencyTracker;
    }
});
const _nocodbsdk = require("nocodb-sdk");
const _Noco = /*#__PURE__*/ _interop_require_default(require("~/Noco"));
const _globals = require("~/utils/globals");
const _DependencySlotMapper = require("~/helpers/DependencySlotMapper");
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {}, sourceKeys = Object.getOwnPropertyNames(source), key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
        target[key] = source[key];
    }
    return target;
}
let DependencyTracker = class DependencyTracker {
    static async trackDependencies(context, dependentType, dependentId, dependencies, ncMeta = _Noco.default.ncMeta, ignoreClear) {
        if (!ignoreClear) {
            await this.clearDependencies(context, dependentType, dependentId, ncMeta);
        }
        const deps = [];
        const sourceTypes = [
            {
                key: 'columns',
                type: _nocodbsdk.DependencyTableType.Column
            },
            {
                key: 'models',
                type: _nocodbsdk.DependencyTableType.Model
            },
            {
                key: 'views',
                type: _nocodbsdk.DependencyTableType.View
            },
            {
                key: 'workflows',
                type: _nocodbsdk.DependencyTableType.Workflow
            }
        ];
        for (const { key, type } of sourceTypes){
            const items = dependencies[key];
            if (items) {
                for (const item of items){
                    const queryableFields = this.extractQueryableFields(dependentType, item);
                    deps.push(_extends({
                        fk_workspace_id: context.workspace_id,
                        base_id: context.base_id,
                        source_type: type,
                        source_id: item.id,
                        dependent_type: dependentType,
                        dependent_id: dependentId
                    }, queryableFields));
                }
            }
        }
        if (deps.length > 0) {
            await ncMeta.bulkMetaInsert(context.workspace_id, context.base_id, _globals.MetaTable.DEPENDENCY_TRACKER, deps);
        }
    }
    /**
   * Generic method to clear all dependencies for a dependent
   */ static async clearDependencies(context, dependentType, dependentId, ncMeta = _Noco.default.ncMeta) {
        await ncMeta.metaDelete(context.workspace_id, context.base_id, _globals.MetaTable.DEPENDENCY_TRACKER, {
            dependent_type: dependentType,
            dependent_id: dependentId
        });
    }
    static async getDependentsBySource(context, sourceType, sourceId, options, ncMeta = _Noco.default.ncMeta) {
        const condition = {
            source_type: sourceType,
            source_id: sourceId
        };
        if (options == null ? void 0 : options.dependentType) {
            condition.dependent_type = options.dependentType;
            if (options.dependentType === _nocodbsdk.DependencyTableType.Workflow) {
                if (options.dependentId) {
                    condition.dependent_id = options.dependentId;
                }
                if (options.nodeType) {
                    const slotId = _DependencySlotMapper.dependencySlotMapper.getSlotId(options.dependentType, 'nodeType');
                    if (slotId) condition[slotId] = options.nodeType;
                }
            }
        }
        const results = await ncMeta.metaList2(context.workspace_id, context.base_id, _globals.MetaTable.DEPENDENCY_TRACKER, {
            condition
        });
        // Hydrate results with logical field names
        return results.map((record)=>{
            if (record.dependent_type) {
                const hydratedFields = this.hydrateQueryableFields(record.dependent_type, record);
                // Remove internal storage fields
                const { queryable_field_0, queryable_field_1, meta } = record, cleanRecord = _object_without_properties_loose(record, [
                    "queryable_field_0",
                    "queryable_field_1",
                    "meta"
                ]);
                return _extends({}, cleanRecord, hydratedFields);
            }
            return record;
        });
    }
    /**
   * Check if deleting a source would break any dependents
   */ static async checkBreakingChanges(context, { sourceType, sourceId }, ncMeta = _Noco.default.ncMeta) {
        const dependents = await this.getDependentsBySource(context, sourceType, sourceId, undefined, ncMeta);
        const affectedMap = new Map();
        for (const dep of dependents){
            affectedMap.set(dep.dependent_id, dep.dependent_type);
        }
        return {
            hasBreakingChanges: dependents.length > 0,
            affected: Array.from(affectedMap.entries()).map(([id, type])=>({
                    type,
                    id
                })),
            dependents
        };
    }
    /**
   * Recursively find all transitive dependents of a source.
   */ static async getTransitiveDependents(context, sourceType, sourceId, maxDepth = 10, ncMeta = _Noco.default.ncMeta) {
        const visited = new Set();
        const result = [];
        // FIXME:recursive CTE for optimization
        const createKey = (type, id)=>`${type}:${id}`;
        const traverse = async (currentType, currentId, depth, pathSoFar)=>{
            if (depth > maxDepth) {
                return;
            }
            const key = createKey(currentType, currentId);
            if (visited.has(key)) {
                return;
            }
            visited.add(key);
            const directDependents = await this.getDependentsBySource(context, currentType, currentId, undefined, ncMeta);
            for (const dep of directDependents){
                const depKey = createKey(dep.dependent_type, dep.dependent_id);
                const currentPath = [
                    ...pathSoFar,
                    depKey
                ];
                if (!visited.has(depKey)) {
                    result.push(_extends({}, dep, {
                        depth,
                        dependencyPath: currentPath
                    }));
                    if (dep.dependent_type === _nocodbsdk.DependencyTableType.Column || dep.dependent_type === _nocodbsdk.DependencyTableType.Model || dep.dependent_type === _nocodbsdk.DependencyTableType.View) {
                        await traverse(dep.dependent_type, dep.dependent_id, depth + 1, currentPath);
                    }
                }
            }
        };
        await traverse(sourceType, sourceId, 1, [
            createKey(sourceType, sourceId)
        ]);
        return result;
    }
    /**
   * Check breaking changes including transitive dependencies.
   */ static async checkTransitiveBreakingChanges(context, sourceType, sourceId, ncMeta = _Noco.default.ncMeta) {
        const transitiveDeps = await this.getTransitiveDependents(context, sourceType, sourceId, 10, ncMeta);
        const affectedMap = new Map();
        for (const dep of transitiveDeps){
            if (!affectedMap.has(dep.dependent_id)) {
                affectedMap.set(dep.dependent_id, {
                    type: dep.dependent_type,
                    id: dep.dependent_id,
                    depth: dep.depth,
                    path: dep.dependencyPath
                });
            }
        }
        return {
            hasBreakingChanges: transitiveDeps.length > 0,
            affected: Array.from(affectedMap.values()),
            totalAffected: transitiveDeps.length
        };
    }
    /**
   * Internal: Extract queryable fields from dependency info based on dependent type
   */ static extractQueryableFields(dependentType, item) {
        return _DependencySlotMapper.dependencySlotMapper.extractSlotFields(dependentType, item);
    }
    /**
   * Internal: Hydrate queryable fields back to logical names
   */ static hydrateQueryableFields(dependentType, record) {
        return _DependencySlotMapper.dependencySlotMapper.hydrateSlotFields(dependentType, record);
    }
    constructor(data){
        Object.assign(this, data);
    }
};
